import { useSPIntl } from 'hooks/useIntl';
import { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { usePlayVideoState } from '../../contexts/PlayVideoContext';
import type { PublicationCategory } from './model';

const StyledDivWithImage = styled.div<{ $backgroundImageUrl?: string }>`
  height: 152px;
  background-image:
    linear-gradient(0deg, rgba(81, 87, 87, 0.2) 30%, rgba(81, 87, 87, 1)), url(${props => props.$backgroundImageUrl});
  background-size: cover;
  background-position: center;
`;

interface SeeMorePublicationItemProps {
  image?: string;
  categoryId?: number;
  title: string;
  documentUrl?: string;
  publishedOn?: string;
  videoId?: string;
  categories?: ReadonlyArray<PublicationCategory>;
}

export const SeeMorePublicationItem: FC<SeeMorePublicationItemProps> = ({
  categories,
  categoryId,
  image,
  videoId,
  title,
  documentUrl,
  publishedOn,
}) => {
  const playState = usePlayVideoState();
  const onOpenDocument = useCallback(() => {
    if (documentUrl !== undefined) {
      window.open(documentUrl);
    }
    if (videoId !== undefined && playState?.isPlaying === false) {
      playState.play(videoId, title);
    }
  }, [documentUrl, videoId, title, playState]);
  const { formatMessage, formatDisplayedDate } = useSPIntl();
  const publishedDate = useMemo(
    () => (publishedOn !== undefined ? formatDisplayedDate(publishedOn) : undefined),
    [formatDisplayedDate, publishedOn],
  );

  const categoryName = useMemo(
    () =>
      categories !== undefined && categoryId !== undefined
        ? categories.find(c => c.id === categoryId)?.name
        : undefined,
    [categories, categoryId],
  );

  const imageUrl = useMemo(
    () => image ?? (videoId !== undefined ? `https://socgen.qumucloud.com/kulu/${videoId}/thumbnail` : undefined),
    [image, videoId],
  );

  return (
    <div className="row" data-e2e="see-more-item">
      <div className="card col-12 mb-4">
        <div className="row">
          <StyledDivWithImage
            className="card-header col col-md-4 bg-lvl2 clickable"
            $backgroundImageUrl={imageUrl}
            onClick={onOpenDocument}
          />
          <div className="card-body col col-md-8 ps-md-4 pb-0">
            {categoryName && <h5>{categoryName}</h5>}
            <a className="text-primary" onClick={onOpenDocument} data-e2e="see-more-item-title">
              <h3>{title}</h3>
            </a>
            <span className="text-secondary">
              {formatMessage({ id: 'discover.products.published' }, { publishedDate })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

SeeMorePublicationItem.displayName = 'SeeMorePublicationItem';
